import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CloseOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Space, Typography, theme } from "antd";
import { runInAction } from "mobx";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useStores } from "../../../../store/stores.js";
import { __ } from "../../../../utils/i18n.js";
import { eraseMemorizedCoupon } from "../../hooks/useRecoverableFormV2.js";
import { CheckoutStep } from "../../store/CheckoutV2Store.js";
const COUPON_FORM_VALUE_KEY = "coupon";
const CouponTrigger = ({ onClick })=>/*#__PURE__*/ _jsx(Typography.Link, {
        onClick: onClick,
        style: {
            fontSize: 13
        },
        children: __("Got a coupon?")
    });
/**
 * `CouponForm` is the expanded input + Apply button surface, rendered
 * separately from the trigger. Before identification, submitted coupons are
 * stashed on the shared `formValues` bag for the identification form to
 * forward on submit; after identification, Apply pushes the code straight
 * into the Paddle incomplete checkout.
 */ const CouponForm = observer(()=>{
    const { checkoutV2Store } = useStores();
    const { token } = theme.useToken();
    // Hydrate from the shared bag so an Edit-Details remount surfaces the
    // previously-entered coupon in its "saved" state (the user didn't remove
    // the code, they just went back to fix their details).
    const seeded = typeof checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY] === "string" ? checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY] : "";
    const [draft, setDraft] = useState(seeded);
    const [savedBeforeIdent, setSavedBeforeIdent] = useState(seeded);
    const isAfterIdentification = checkoutV2Store.step >= CheckoutStep.IdentificationSubmitted;
    const { invalidCouponNotice } = checkoutV2Store;
    // A backend-rejected coupon clears itself — the local "saved" badge must
    // disappear alongside the shared form bag, otherwise the input would show
    // two contradictory states (saved green vs. invalid warning).
    useEffect(()=>{
        if (invalidCouponNotice) {
            setSavedBeforeIdent("");
        }
    }, [
        invalidCouponNotice
    ]);
    const clearSavedCoupon = useCallback(()=>{
        runInAction(()=>{
            delete checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY];
        });
        // Drop the coupon from the persisted form cache too, otherwise a
        // later visit (same device, within the 48 h TTL) would resurrect
        // the code the buyer just explicitly cleared.
        eraseMemorizedCoupon();
        setSavedBeforeIdent("");
        setDraft("");
        checkoutV2Store.setInvalidCouponNotice(undefined);
    }, [
        checkoutV2Store
    ]);
    const handleApply = useCallback(()=>{
        const code = draft.trim();
        if (!code) {
            return;
        }
        if (isAfterIdentification && checkoutV2Store.transaction) {
            // After identification: push the coupon into the incomplete
            // checkout and let Paddle re-open with it; Paddle's own `Coupon.*`
            // events sync the computed savings back into the transaction.
            checkoutV2Store.transaction.syncCouponWithIncompleteCheckout(code);
        } else {
            // "Before" mode: stash on the shared formValues bag so the
            // identification form can pick it up on submit.
            runInAction(()=>{
                checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY] = code;
            });
            setSavedBeforeIdent(code);
            // A new entry implicitly retires the previous invalid-coupon notice.
            checkoutV2Store.setInvalidCouponNotice(undefined);
        }
    }, [
        checkoutV2Store,
        isAfterIdentification,
        draft
    ]);
    // A saved (pre-identification) coupon swaps Apply → clear (X), so the
    // buyer can drop a wrong code without having to manually wipe the input.
    // Width tracks Apply's footprint so neither version shrinks the input.
    const primaryAction = savedBeforeIdent ? /*#__PURE__*/ _jsx(Button, {
        onClick: clearSavedCoupon,
        className: "checkout-v2-coupon-clear",
        "aria-label": __("Remove coupon"),
        icon: /*#__PURE__*/ _jsx(CloseOutlined, {}),
        children: __("Remove")
    }) : /*#__PURE__*/ _jsx(Button, {
        type: "primary",
        onClick: handleApply,
        className: "checkout-v2-coupon-apply",
        style: {
            fontWeight: 600
        },
        children: __("Apply")
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Space.Compact, {
                style: {
                    width: "100%"
                },
                children: [
                    /*#__PURE__*/ _jsx(Input, {
                        value: savedBeforeIdent || draft,
                        placeholder: __("Coupon code"),
                        onChange: (event)=>setDraft(event.target.value),
                        onPressEnter: handleApply,
                        disabled: !!savedBeforeIdent
                    }),
                    primaryAction
                ]
            }),
            !isAfterIdentification && savedBeforeIdent && !invalidCouponNotice && /*#__PURE__*/ _jsx("div", {
                role: "status",
                "aria-live": "polite",
                style: {
                    marginTop: 8,
                    color: token.colorSuccess,
                    fontSize: 12
                },
                children: __("Coupon saved. We'll check it at the payment step.")
            }),
            invalidCouponNotice && /*#__PURE__*/ _jsx(Alert, {
                type: "warning",
                showIcon: true,
                message: invalidCouponNotice,
                style: {
                    marginTop: 8,
                    padding: "6px 10px"
                }
            })
        ]
    });
});
export { CouponTrigger, CouponForm, COUPON_FORM_VALUE_KEY };
